import { parseLocalDate, todayString } from "../../utils/dateUtils";

// Comment mentions are stored as @[Display Name](userId); keep in sync with
// MENTION_PATTERN in backend/src/services/tasks.service.js.
const MENTION_PATTERN = /@\[([^\]\n]{1,200})\]\(([0-9a-fA-F-]{36})\)/g;

// Splits a stored comment body into text and mention segments for rendering.
export function splitMentions(body) {
  const parts = [];
  let last = 0;
  for (const match of body.matchAll(MENTION_PATTERN)) {
    if (match.index > last) {
      parts.push({ type: "text", value: body.slice(last, match.index) });
    }
    parts.push({ type: "mention", name: match[1], userId: match[2] });
    last = match.index + match[0].length;
  }
  if (last < body.length) parts.push({ type: "text", value: body.slice(last) });
  return parts;
}

// Turns the composer's plain "@Name" text into stored mention markup for the
// people picked from the suggestion list (longest names first so "@Ann Lee"
// wins over "@Ann").
export function encodeMentions(text, mentions) {
  const unique = [...new Map(mentions.map((m) => [m.id, m])).values()].sort(
    (a, b) => b.name.length - a.name.length,
  );
  let result = text;
  for (const person of unique) {
    const escaped = person.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(
      new RegExp(`(^|[^\\w\\]])@${escaped}(?![\\w])`, "g"),
      `$1@[${person.name}](${person.id})`,
    );
  }
  return result;
}

export function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase() || "?";
}

// "today" | "overdue" | "upcoming" | null — drives due date colouring.
export function dueState(dueDate, completed) {
  if (!dueDate || completed) return null;
  const today = todayString();
  if (dueDate < today) return "overdue";
  if (dueDate === today) return "today";
  return "upcoming";
}

export function formatDueDate(dueDate) {
  if (!dueDate) return "";
  const today = todayString();
  if (dueDate === today) return "Today";
  const date = parseLocalDate(dueDate);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dueDate === tomorrow.toLocaleDateString("en-CA")) return "Tomorrow";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function formatTimestamp(value) {
  const date = new Date(value);
  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function describeActivity(activity) {
  const d = activity.data || {};
  switch (activity.type) {
    case "CREATED":
      if (d.repeatedFromId) return "created this as the next repeat of the task";
      return d.parentTaskName
        ? `created this task as a subtask of ${d.parentTaskName}`
        : "created this task";
    case "RENAMED":
      return `changed the name to "${d.to}"`;
    case "DESCRIPTION_CHANGED":
      return "changed the description";
    case "DUE_DATE_CHANGED":
      return d.to
        ? `changed the due date to ${formatDueDate(d.to)}`
        : "removed the due date";
    case "ASSIGNED":
      return d.toName
        ? `assigned this task to ${d.toName}`
        : "unassigned this task";
    case "COMPLETED":
      return "marked this task complete";
    case "REOPENED":
      return "marked this task incomplete";
    case "PROJECT_ADDED":
      return `added this task to ${d.projectName}`;
    case "PROJECT_REMOVED":
      return `removed this task from ${d.projectName}`;
    case "DEPENDENCY_ADDED":
      return `marked this task as waiting on "${d.taskName}"`;
    case "DEPENDENCY_REMOVED":
      return `removed the dependency on "${d.taskName}"`;
    case "SUBTASK_ADDED":
      return `added subtask "${d.subtaskName}"`;
    case "TYPE_CHANGED":
      return d.to === "task"
        ? "turned this back into a task"
        : `marked this as ${d.to === "milestone" ? "a milestone" : "an approval"}`;
    case "APPROVAL_DECIDED":
      return (
        {
          approved: "approved this",
          changes_requested: "requested changes",
          rejected: "rejected this",
          pending: "reset the approval to pending",
        }[d.decision] || "updated the approval"
      );
    case "RECURRENCE_CHANGED":
      return d.to
        ? `set this to repeat ${describeRecurrence(d.to).toLowerCase()}`
        : "stopped this task repeating";
    case "REPEAT_SCHEDULED":
      return d.dueDate
        ? `completed a repeat — the next one is due ${formatDueDate(d.dueDate)}`
        : "completed a repeat — the next one was created";
    case "SECTION_MOVED":
      return d.sectionName
        ? `moved this to "${d.sectionName}" in ${d.projectName}`
        : `removed this from its section in ${d.projectName}`;
    case "TIME_LOGGED":
      return `logged ${formatMinutes(d.minutes)} for ${formatDueDate(d.date)}`;
    case "TIME_LOG_REMOVED":
      return `removed ${formatMinutes(d.minutes)} logged for ${formatDueDate(d.date)}`;
    case "ATTACHMENT_ADDED":
      return `attached "${d.fileName}"`;
    case "ATTACHMENT_REMOVED":
      return `removed the attachment "${d.fileName}"`;
    case "COLLABORATOR_ADDED":
      return d.via === "mention"
        ? `mentioned ${d.userName}, adding them as a collaborator`
        : `added ${d.userName} as a collaborator`;
    default:
      return "updated this task";
  }
}

// Repeat rules: { freq: "daily" | "weekly" | "monthly", interval, weekdays? }
// with weekdays 0 = Sunday; keep in sync with backend/src/utils/taskRecurrence.js.
export const WEEKDAYS = [
  { day: 1, short: "M", name: "Mon" },
  { day: 2, short: "T", name: "Tue" },
  { day: 3, short: "W", name: "Wed" },
  { day: 4, short: "T", name: "Thu" },
  { day: 5, short: "F", name: "Fri" },
  { day: 6, short: "S", name: "Sat" },
  { day: 0, short: "S", name: "Sun" },
];

const plural = (n, unit) => (n === 1 ? unit : `${n} ${unit}s`);

// { freq: "weekly", interval: 1, weekdays: [1, 3, 5] } -> "Weekly on Mon, Wed, Fri"
export function describeRecurrence(rule) {
  if (!rule) return "";
  const n = rule.interval || 1;
  if (rule.freq === "daily") return n === 1 ? "Daily" : `Every ${n} days`;
  if (rule.freq === "monthly") return n === 1 ? "Monthly" : `Every ${n} months`;
  const days = rule.weekdays || [];
  const names = WEEKDAYS.filter((w) => days.includes(w.day)).map((w) => w.name);
  const on =
    days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))
      ? "on weekdays"
      : days.length === 7
        ? "on every day"
        : `on ${names.join(", ")}`;
  return `${n === 1 ? "Weekly" : `Every ${plural(n, "week")}`} ${on}`;
}

export const TASK_TYPE_LABELS = {
  task: "Task",
  milestone: "Milestone",
  approval: "Approval",
};

export const APPROVAL_LABELS = {
  pending: "Pending approval",
  approved: "Approved",
  changes_requested: "Changes requested",
  rejected: "Rejected",
};

// 90 -> "1h 30m", 45 -> "45m", 120 -> "2h".
export function formatMinutes(total) {
  const minutes = Math.max(0, Math.round(total || 0));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function apiError(err, fallback = "Something went wrong") {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}
